import React, { useState } from "react";
import { get } from "lodash";
import { MODULE_DISPLAY_NAMES } from "../utils/constants";
import SearchContainer from "../components/SearchContainer";

// Helper for risk score background - subtle purple shades instead of gradients
const getRiskColor = (score, hasError) => {
  if (hasError || score === null || score === undefined) return "#6b7280"; // gray-500

  if (score >= 75) return "#7c3aed"; // purple-700
  if (score >= 50) return "#a78bfa"; // purple-400
  if (score >= 25) return "#c4b5fd"; // purple-300
  return "#ddd6fe"; // purple-100
};

const generateSummary = (module, rawData) => {
  if (!module || !rawData) return "No summary available.";

  const error = get(rawData, "error");
  if (error) return `Error: ${error}`;

  module = module.toLowerCase();

  switch (module) {
    case "localdb": {
      const LocalDBData = Array.isArray(rawData) ? rawData[0] : null;
      const dbmessage = get(rawData.summary, "note");

      if (!LocalDBData && dbmessage) return dbmessage;

      if (LocalDBData) {
        const confidence = get(LocalDBData, "confidence");
        const risk_score = get(LocalDBData, "risk_score");
        const allSources = get(LocalDBData, "sources", []);
        
        // Filter out "user"
        const filteredSources = Array.isArray(allSources)
          ? allSources.filter((s) => s.toLowerCase() !== "user")
          : [];

        const status = get(LocalDBData, "status");
        return `Confidence: ${confidence} | Risk: ${risk_score} | Sources: ${filteredSources.join(", ")} | Status: ${status}`;
      }

      return "No summary available.";
    }
    case "malwarebazaar": {
      const error = get(rawData, "error");
      if (error) return `Error: ${error}`;

      if (!rawData || !Array.isArray(rawData)) {
        return "No data available";
      }

      if (rawData.length === 0) {
        return "No data available";
      }

      const entry = rawData[0];
      const malwareFamily = entry.signature || "Unknown family";
      const fileType = entry.file_type || "Unknown type";
      const fileSize = typeof entry.file_size === "number" ? `${entry.file_size} bytes` : "Unknown size";
      const fileName = entry.file_name || "Unnamed file";
      const lastSeen = entry.last_seen || entry.first_seen || "Unknown";

      return `Family: ${malwareFamily} | Type: ${fileType} | Size: ${fileSize} | File: ${fileName} | Last Seen: ${lastSeen}`;
    }

    case "abuseipdb": {
      const raw = get(rawData, "data", {});// FIXED PATH
      const confidence = get(raw, "abuseConfidenceScore", null);
      const totalReports = get(raw, "totalReports", null);
      const countryCode = get(raw, "countryCode", "Unknown");
      const isTor = get(raw, "isTor", false);
      const hasConfidence = confidence !== null && confidence !== undefined;
      const hasReports = totalReports !== null && totalReports !== undefined;

      if (hasConfidence && hasReports) {
        return `${confidence}% confidence, ${totalReports} reports, ${countryCode}, ${
          isTor ? "TOR user" : "Not TOR user"
        }`;
      }

      return "No summary available.";
    }


    case "googlesafebrowse": {
      const summary = get(rawData, "summary");
      const threatTypes = summary?.threat_types ?? [];
      const platformTypes = summary?.platform_types ?? [];
      const risk = rawData?.risk_score;

      const threats = threatTypes.length ? threatTypes.join(", ") : "Unknown";
      const platform = platformTypes.length ? platformTypes.join(", ") : "Unknown platform";

      if (threatTypes.length || platformTypes.length || risk !== undefined) {
        return `Detected threat(s): ${threats} on ${platform}. Risk score: ${risk}`;
      }
      return "No summary available.";
    }
    case "virustotal": {
      const attributes = get(rawData, "data.attributes", {});
      const reputation = get(attributes, "reputation");
      const lastAnalysisDate = get(attributes, "last_analysis_date");
      const stats = get(attributes, "last_analysis_stats", {});
      return reputation !== undefined
        ? `Reputation: ${reputation} | Malicious: ${stats.malicious} | Suspicious: ${stats.suspicious} | Harmless: ${stats.harmless} | Last analyzed: ${new Date(
            lastAnalysisDate * 1000
          ).toLocaleString()}`
        : "No summary available.";
    }
    case "greynoise": {
      const error = get(rawData, "error");
      if (error) return `Error: ${error}`;

      const classification = get(rawData, "classification");
      if (classification) {
        return `Classification: ${classification}`;
      }

      const message = get(rawData, "message");
      if (message && message.toLowerCase() !== "success") {
        return message;
      }

      return "No summary available.";
    }


    case "shodan": {
      const ip = get(rawData, "data.ip");
      const country = get(rawData, "data.country_name");
      const org = get(rawData, "data.org");
      const ports = get(rawData, "data.ports", []).length;
      const vulns = Object.keys(get(rawData, "data.vulns", {})).length;
      const shodanMessage = get(rawData, "message");
      if (shodanMessage) return shodanMessage;
      return ip && country && org
        ? `IP: ${ip}, Country: ${country}, Org: ${org}, Ports: ${ports}, Vulns: ${vulns}`
        : "No summary available.";
    }
    case "urlhaus": {
      const urlhausData = rawData?.[0];
      if (urlhausData?.match) {
        return "Match";
      }
      return "No data found";
    }
    case "onionoo": {
      const torMessage = get(rawData, "message");
      const torExitNode = get(rawData, "tor_exit_node");
      const ipAddress = get(rawData, "ip");
      if (torMessage) return torMessage;
      if (ipAddress !== undefined && torExitNode !== undefined) {
        return `IP: ${ipAddress} | TOR Exit Node: ${torExitNode ? "Yes" : "No"}`;
      }
      return "No summary available.";
    }
    case "whois": {
      const whoisData = get(rawData, "whoisData");
      if (whoisData) {
        const { org, isp, country, city, region, country_code, asn, domain, type } = whoisData.data || {};
        return `Organization: ${org || "Unknown"} | ISP: ${isp || "Unknown"} | Country: ${
          country || "Unknown"
        } | City: ${city || "Unknown"} | Region: ${region || "Unknown"} | Country Code: ${
          country_code || "Unknown"
        } | ASN: ${asn || "Unknown"} | Domain: ${domain || "Unknown"} | Type: ${type || "Unknown"}`;
      }
      return "No summary available.";
    }
    case "threatfox": {
      const threatData = get(rawData, "data", [])[0];
      const message = get(rawData, "message");
      if (!threatData && message) return message;
      if (threatData) {
        const confidenceLevel = get(threatData, "confidence_level");
        const threatType = get(threatData, "threat_type", "Unknown");
        const malwarePrintable =
          get(threatData, "malware_printable") || get(threatData, "malware") || "Unknown";
        const firstSeen = get(threatData, "first_seen");
        return `Threat Type: ${threatType} | Malware: ${malwarePrintable} | Confidence: ${confidenceLevel}% | First Seen: ${
          firstSeen || "Unknown"
        }`;
      }
      return "No summary available.";
    }
    default:
      return "No summary available.";
  }
};

const SearchResultsTable = ({
    results,
    indicator,
    searchCount,
    loadingSearchCount,
    searchCountError,
    overallScore 
  }) => {
  const [sortBy, setSortBy] = useState("module");
  const [sortDirection, setSortDirection] = useState("asc");
  const [activeTab, setActiveTab] = useState("stats");

  const handleSort = (column) => {
    const direction = sortBy === column && sortDirection === "asc" ? "desc" : "asc";
    setSortBy(column);
    setSortDirection(direction);
  };

  const handleDownload = (module, rawData) => {
    const jsonBlob = new Blob([JSON.stringify(rawData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(jsonBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${module}_data.json`;
    link.click();
    URL.revokeObjectURL(url);
  };
  
  
  // Convert results object into array for sorting, adding module name as a property
  const resultsArray = Object.entries(results).map(([module, data]) => {
    if (typeof data !== "object" || data === null) {
      console.warn(`Malformed result for ${module}:`, data); // optional
      return {
        module,
        raw: { error: `Invalid data type: ${typeof data}` },
        risk_score: null,
      };
    }

    return {
      module,
      ...data,
    };
  });

  // Sort results array by sortBy property (default "module")
  const sortedResults = resultsArray.sort((a, b) => {
    const aVal = a[sortBy] ?? "";
    const bVal = b[sortBy] ?? "";
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Calculate overall stats
  const validScores = resultsArray
    .map((r) => r.risk_score)
    .filter((score) => typeof score === "number" && !isNaN(score));

  const positiveMatches = validScores.filter((score) => score > 0).length;
  const activeModules = resultsArray.length;
  const highestRisk = validScores.length ? Math.max(...validScores) : 0;


 return (
    <SearchContainer>
      <div className="flex justify-center mt-6">
        <div className="w-full max-w-6xl bg-[#181825] p-6 rounded-xl shadow-md">
          {/* Tabs */}
          <div className="mb-5 border-b border-[#2f2f46]">
            <ul className="flex space-x-6 text-sm font-semibold text-gray-400">
              {["stats", "details", "info"].map((tab) => (
                <li key={tab}>
                  <button
                    onClick={() => setActiveTab(tab)}
                    className={`pb-2 border-b-2 transition ${
                      activeTab === tab
                        ? "border-purple-500 text-purple-300"
                        : "border-transparent hover:text-purple-300 hover:border-purple-500"
                    }`}
                  >
                    {tab === "stats"
                      ? "Stats"
                      : tab === "details"
                      ? "Details"
                      : "Indicator Info"}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Content */}
          {activeTab === "stats" && (
             <div className="grid grid-cols-1 sm:grid-cols-5 gap-6 text-center">
              <div
                className="p-6 rounded-lg text-gray-100 border border-[#2f2f46]"
                style={{ backgroundColor: getRiskColor(overallScore) }} // ← use new value
              >
                <h3 className="text-lg font-medium mb-2">Overall Risk Score (Experimental)</h3>
                <p className="text-4xl font-bold">
                  {overallScore !== null ? overallScore : "N/A"}
                </p>
              </div>
              <div className="p-6 bg-[#1f1f2e] border border-[#2f2f46] rounded-lg text-gray-300">
                <h3 className="text-lg font-medium mb-2">Active Modules</h3>
                <p className="text-4xl font-bold">{activeModules}</p>
              </div>
              <div className="p-6 bg-[#1f1f2e] border border-[#2f2f46] rounded-lg text-gray-300">
                <h3 className="text-lg font-medium mb-2">Positive Matches</h3>
                <p className="text-4xl font-bold">{positiveMatches}</p>
              </div>
              <div className="p-6 bg-[#1f1f2e] border border-[#2f2f46] rounded-lg text-gray-300">
                <h3 className="text-lg font-medium mb-2">Highest Risk Score</h3>
                <p className="text-4xl font-bold">{highestRisk}</p>
              </div>
              <div className="p-6 bg-[#1f1f2e] border border-[#2f2f46] rounded-lg text-gray-300">
                <h3 className="text-lg font-medium mb-2">Search Count</h3>
                {loadingSearchCount ? (
                  <p className="text-2xl font-bold">Loading...</p>
                ) : searchCountError ? (
                  <p className="text-red-500">{searchCountError}</p>
                ) : (
                  <p className="text-4xl font-bold">{searchCount ?? "N/A"}</p>
                )}
              </div>
            </div>
          )}
        {activeTab === "details" && (
          <div className="overflow-x-auto rounded-lg mt-4 border border-[#2f2f46]">
            <table className="w-full text-sm text-left text-gray-300">
              <thead className="bg-[#1f1f2e] uppercase text-gray-400 text-xs select-none">
                <tr>
                  <th className="px-5 py-3"></th>
                  <th
                    className="px-6 py-3 cursor-pointer hover:text-purple-400"
                    onClick={() => handleSort("module")}
                    aria-sort={sortBy === "module" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
                  >
                    Module {sortBy === "module" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    className="px-6 py-3 cursor-pointer hover:text-purple-400"
                    onClick={() => handleSort("risk_score")}
                    aria-sort={
                      sortBy === "risk_score" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"
                    }
                  >
                    Risk Score {sortBy === "risk_score" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    className="px-6 py-3 cursor-pointer hover:text-purple-400"
                    onClick={() => handleSort("summary")}
                    aria-sort={
                      sortBy === "summary" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"
                    }
                  >
                    Summary {sortBy === "summary" && (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedResults
                  .filter((result) => !["ipwhois", "domainwhois"].includes(result.module))
                  .map((result, i) => {
                    const { module, risk_score, raw } = result;
                    const summary = generateSummary(module, raw);
                    return (
                      <tr
                        key={i}
                        className="border-b border-[#2f2f46] hover:bg-[#222232] transition"
                        tabIndex={0}
                      >
                        <td className="px-5 py-3">
                          <span
                            className="inline-block w-4 h-4 rounded-full"
                            style={{ backgroundColor: getRiskColor(risk_score) }}
                            title={`Risk Score: ${risk_score ?? "N/A"}`}
                          />
                        </td>
                        <td className="px-6 py-3 font-semibold text-white">
                          {MODULE_DISPLAY_NAMES[module] || module}
                        </td>
                        <td className="px-6 py-3">{risk_score ?? "N/A"}</td>
                        <td className="px-6 py-3 whitespace-pre-wrap">{summary}</td>
                        <td className="px-6 py-3">
                          <button
                            onClick={() => handleDownload(module, raw)}
                            className="text-purple-400 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-400 rounded"
                            aria-label={`Download JSON data for ${module}`}
                          >
                            Download JSON
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "info" && (
  <div className="p-4 mt-4 bg-[#1f1f2e] rounded-lg text-gray-300">
    <h2 className="text-xl mb-3 text-purple-400 font-semibold">Indicator Information</h2>
    <p className="mb-4">This section displays WHOIS or enrichment data about the queried indicator.</p>
    {sortedResults.map((result) => {
      const { module, raw } = result;

          // IP WHOIS display
          if (module === "ipwhois" && raw) {
            const { connection, country, city, region, country_code, type } = raw;
            const whoisData = {
              org: connection?.org || "Unknown",
              isp: connection?.isp || "Unknown",
              country: country || "Unknown",
              city: city || "Unknown",
              region: region || "Unknown",
              country_code: country_code || "Unknown",
              asn: connection?.asn || "Unknown",
              domain: connection?.domain || "Unknown",
              type: type || "Unknown",
            };
            return (
              <div key={result.module} className="space-y-1 mb-6">
                <h3 className="text-lg font-semibold text-purple-300">IP WHOIS</h3>
                <p><strong>Organization:</strong> {whoisData.org}</p>
                <p><strong>ISP:</strong> {whoisData.isp}</p>
                <p><strong>Country:</strong> {whoisData.country}</p>
                <p><strong>City:</strong> {whoisData.city}</p>
                <p><strong>Region:</strong> {whoisData.region}</p>
                <p><strong>Country Code:</strong> {whoisData.country_code}</p>
                <p><strong>ASN:</strong> {whoisData.asn}</p>
                <p><strong>Domain:</strong> {whoisData.domain}</p>
                <p><strong>Type:</strong> {whoisData.type}</p>
              </div>
            );
          }

          // DOMAIN WHOIS display
          if (module === "domainwhois" && raw) {
            const registrar = raw?.registrar || "Unknown";
            const creation = raw?.creation_date || "Unknown";
            const expiration = raw?.expiration_date || "Unknown";
            const name_servers = Array.isArray(raw?.name_servers)
              ? raw.name_servers.join(", ")
              : raw?.name_servers || "Unknown";
            const emails = Array.isArray(raw?.emails)
              ? raw.emails.join(", ")
              : raw?.emails || "Unknown";

            return (
              <div key={result.module} className="space-y-1 mb-6">
                <h3 className="text-lg font-semibold text-purple-300">Domain WHOIS</h3>
                <p><strong>Registrar:</strong> {registrar}</p>
                <p><strong>Creation Date:</strong> {creation}</p>
                <p><strong>Expiration Date:</strong> {expiration}</p>
                <p><strong>Name Servers:</strong> {name_servers}</p>
                <p><strong>Emails:</strong> {emails}</p>
              </div>
            );
          }

          return null;
        })}
      </div>
    )}

      </div>
    </div>
  </SearchContainer>
);
};

export default SearchResultsTable;
